import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from 'react-query';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { createFolderValidation } from './CreateFolder.validation';
import { useCreateFolderMutation } from '../../api/useCreateFolderMutation';
import { useRouteParams } from '../../hooks/useRouteParams';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { MAX_FOLDER_NAME_LENGTH, ProviderType } from '../../DocumentVault.meta';
import { useNavigate } from '../../hooks/useNavigate';
import { useFilterableYearOption } from '../../state/selectors';
import { queryKeys } from '../../api/queryKeys';

interface CreateFolderFields {
  folderName: string;
}

interface Props {
  onClose(): void;
}

export const CreateFolder: React.FC<Props> = ({ onClose }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<CreateFolderFields>({
    defaultValues: {
      folderName: '',
    },

    resolver: yupResolver(createFolderValidation),
  });

  const { clientId, path, providerType, filterableByYear } = useRouteParams();

  const { value: year } = useFilterableYearOption();

  const queryClient = useQueryClient();

  const {
    mutateAsync: createFolder,
    isLoading: isCreatingFolder,
    error: createFolderError,
  } = useCreateFolderMutation();

  const navigate = useNavigate();

  const onSubmit = async ({ folderName }) => {
    if (providerType === ProviderType.CloudStorage) {
      // The folder is created when a file is added to it.
      navigate({
        path: `${path}${encodeURIComponent(`${folderName}/`)}`,
        providerType,
      });

      // Invalidate the parent folder's cache to ensure the newly created folder appears
      // in the list after a file is uploaded.
      queryClient.invalidateQueries(queryKeys.listDocumentVaultEntities(clientId, path));

      onClose();
    } else {
      const folderPath = `${decodeURIComponent(path)}${folderName}/`;

      try {
        const currentYear = filterableByYear ? year : null;
        await createFolder({
          clientId,
          folderData: {
            folderPath,
            path,
            providerType,
            currentYear,
          },
        });

        queryClient.refetchQueries(
          queryKeys.listDocumentVaultEntities(clientId, path, providerType, currentYear),
          {
            active: true,
          },
        );

        onClose();
      } catch {
        // Ignore
      }
    }
  };

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>New Folder</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {createFolderError && (
            <Alert message={createFolderError.message} variant="error" my="16" />
          )}

          <Input
            {...register('folderName')}
            type="text"
            placeholder="New Folder Name"
            formVariant={errors.folderName ? 'error' : undefined}
            helperText={errors.folderName ? errors.folderName.message : ''}
            sizeVariant="large"
            disabled={isCreatingFolder}
            aria-label="Folder name"
            maxLength={MAX_FOLDER_NAME_LENGTH}
            mt="8"
          />
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" ml="12" isLoading={isCreatingFolder}>
            Create
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
